import jsCookie from 'js-cookie';
import isEqual from 'lodash/isEqual';
import pickBy from 'lodash/pickBy';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  gtmLoginModule,
  gtmSignUpModule,
} from 'modules/gtm/modules/sections/auth';
import {
  UpdateUserDataResponse,
  UserDataFields,
  UserState,
} from 'modules/shop/user/types';
import storage from 'modules/storage';
import {
  guestRegister,
  login,
  logout,
  register,
} from 'commerce/scayle/auth/client';
import {
  createCustomerAddress,
  getCustomerAddresses,
  getCustomerInformation,
  updateAddressById,
  updateCompany,
  updateEmail,
  updatePassword,
  updatePersonalData,
} from 'commerce/scayle/checkout';
import { GENDER } from 'commerce/scayle/constants';
import { setCompanyName } from 'commerce/scayle/user-interaction';
import { FetchResponse } from 'commerce/utils/network';

interface ICreateUser {
  gtm: typeof gtmLoginModule & typeof gtmSignUpModule;
}

const createUser = ({ gtm }: ICreateUser) =>
  create(
    devtools<UserState>((set, getState) => ({
      user: undefined,
      loading: false,
      addresses: null,
      async login(credentials) {
        const { error } = await login(credentials);

        if (!error) {
          gtm.gtmPushSuccessLogin();
          return getState().getUser();
        }

        gtm.gtmPushFailedLogin(error?.message);
        return null;
      },
      async logout() {
        const { error } = await logout();
        storage.local.removeItem('wishlist');
        !error && set({ user: null });
      },
      async register(data) {
        set({ loading: true });

        const { error } = await register(data);
        const user = !error ? await getState().getUser() : null;

        if (!error) {
          gtm.gtmPushSuccessSignUp();
        } else {
          gtm.gtmPushFailedSignUp(error?.message);
        }

        set({ loading: false });

        return { user, error };
      },
      async guestRegister(data) {
        const { error, response } = await guestRegister(data);
        const user = !error ? await getState().getUser() : null;
        return { user, error: error, response };
      },
      async createAddress(customerId, billing, shipping) {
        if (!!shipping) {
          return Promise.all<FetchResponse<{}>>([
            createCustomerAddress(customerId, {
              ...billing,
              isDefault: {
                shipping: false,
                billing: true,
              },
            }),
            createCustomerAddress(customerId, {
              ...shipping,
              isDefault: {
                shipping: true,
                billing: false,
              },
            }),
          ]).then((res) => {
            return res.find((o) => o.error);
          }) as Promise<FetchResponse<{}>>;
        } else {
          return createCustomerAddress(customerId, billing);
        }
      },
      async updateCompany(billing) {
        if (billing?.gender === GENDER.COMPANY) {
          /** set company name for order custom data for scayle */
          setCompanyName(billing.company);
          return await updateCompany(billing.company, billing.tax);
        }

        return null;
      },
      async getUser() {
        set({ loading: true });
        const user = await getCustomerInformation();
        set({ loading: false });

        /** remove user if cookies expired */
        if (!user) {
          jsCookie.remove('access_token');
          jsCookie.remove('refresh_token');
        }

        if (!user?.status?.isGuestCustomer) {
          set({ user });
          return user;
        }

        return user;
      },
      async update({
        firstName,
        lastName,
        gender,
        zip,
        city,
        street,
        email,
        street2,
        addressId,
        company,
        tax,
        password,
        newPassword,
        isDefault,
        shouldUpdatePersonalData = true,
      }: UserDataFields) {
        const { user, addresses } = getState();
        const responses: UpdateUserDataResponse = {
          email: {},
          password: {},
          personalData: {},
          address: {},
          company: {},
        };

        if (!user && !addresses) {
          return null;
        }

        set({ loading: true });

        /** Update Email if changed */
        if (email && user?.email !== email) {
          responses.email = await updateEmail(email);
        }

        /** Update Main Personal Data if allowed */
        if (shouldUpdatePersonalData) {
          const oldPersonalData = {
            firstName: user?.firstName,
            lastName: user?.lastName,
            gender: user?.gender,
          };
          const newPersonalData = {
            firstName: firstName || user?.firstName || '',
            lastName: lastName || user?.lastName || '',
            gender: gender || user?.gender,
          };

          if (!isEqual(newPersonalData, oldPersonalData)) {
            responses.personalData = await updatePersonalData(newPersonalData);
          }
        }

        /** Update Address */
        if (addressId && addresses) {
          const currentAddress = addresses.find(
            (addr) => addr.id === addressId,
          );

          if (currentAddress && street) {
            const streetParts = street.trim().split(' ');
            const houseNumber = streetParts.length > 1 ? streetParts.pop() : '';
            const baseStreet = streetParts.join(' ');

            const oldAddressData = {
              city: currentAddress.city,
              street: currentAddress.street,
              zipCode: currentAddress.zipCode,
              additional: currentAddress.additional,
              isDefault: {
                billing: currentAddress.isBillingAddress,
                shipping: currentAddress.isShippingAddress,
              },
              recipient: {
                firstName: currentAddress.recipient.firstName,
                lastName: currentAddress.recipient.lastName,
                gender: currentAddress.recipient.gender,
              },
            };

            const addressData = {
              city: city ?? currentAddress.city,
              street: baseStreet || currentAddress.street,
              houseNumber: houseNumber || currentAddress.houseNumber,
              zipCode: zip ?? currentAddress.zipCode,
              additional: street2 || currentAddress.additional || ' ',
              recipient: {
                firstName: firstName ?? currentAddress.recipient.firstName,
                lastName: lastName ?? currentAddress.recipient.lastName,
                gender: gender ?? currentAddress.recipient.gender,
              },
              isDefault,
            };

            /** Update address if changed */
            if (!isEqual(oldAddressData, addressData)) {
              responses.address = await updateAddressById(addressId, {
                ...addressData,
                countryCode: currentAddress.countryCode,
              });
            }
            await getState().getAddresses();
          }
        }

        /** Update company */
        if (gender === GENDER.COMPANY && company && tax) {
          /** set company name for order custom data for scayle */
          setCompanyName(company);
          responses.company = await updateCompany(company, tax);
        }

        /** Update Password */
        if (password?.length && newPassword?.length) {
          responses.password = await updatePassword(password, newPassword);
        }

        set({ loading: false });

        return pickBy(
          responses,
          (obj) => obj.error !== undefined,
        ) as UpdateUserDataResponse;
      },

      async getAddresses() {
        set({ loading: true });

        const addresses = await getCustomerAddresses();
        set({ addresses, loading: false });
        return addresses;
      },
    })),
  );

export const useUser = createUser({
  gtm: { ...gtmLoginModule, ...gtmSignUpModule },
});
